import { css } from '@emotion/core';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tooltip
} from '@material-ui/core';
import { AvatarGroup } from '@material-ui/lab';
import firebase from 'firebase/app';
import { compact, sortBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { ExternalLink, Check } from 'react-feather';
import { useDialogState } from '../hooks/useDialogState';
import { useCurrentUser } from '../services/currentUser';
import { LoadingValue } from '../services/db';
import { Doc } from '../services/document';
import { EMPTY_ARR } from '../services/emptyConstants';
import {
  rsvpForEvent,
  unRsvpForEvent,
  useRecentRsvpsForEvent,
  useRsvpForEvent,
  useRsvpsForEvent
} from '../services/eventRsvps';
import {
  toUserName,
  useUserPublicMapByIds,
  useUsersPublicByIds
} from '../services/usersPublic';
import styled from '../styled';
import { IEvent } from '../types/Event';
import { UserPublic } from '../types/User';
import { PlainLoader } from './Loader';
import { SignupOrLoginDialog } from './LoginForm';

const EventDetails = styled('a')`
  display: inline-block;
  font-family: ${(p) => p.theme.typography.fontFamily};
  line-height: 1rem;
  font-size: 0.9rem;
  border-radius: ${(p) => p.theme.shape.borderRadius}px;
  cursor: pointer;
  padding: 5px;
  color: ${(p) => p.theme.palette.primary.main};
  margin-top: ${(p) => p.theme.spacing(3)}px;
  margin-left: ${(p) => p.theme.spacing(1)}px;
  position: relative;

  svg {
    position: relative;
    top: 1px;
    left: 4px;
  }
`;

const ToggleButton = styled('button')`
  font-family: ${(p) => p.theme.typography.fontFamily};
  line-height: 1rem;
  font-size: 0.9rem;
  border-radius: ${(p) => p.theme.shape.borderRadius}px;
  cursor: pointer;
  padding: 6px 12px;
  color: ${(p) => p.theme.palette.primary.main};
  font-weight: bold;
  margin-top: ${(p) => p.theme.spacing(3)}px;
`;

const ToggleOff = styled(ToggleButton)`
  border: 1px solid ${(p) => p.theme.palette.primary.dark};
  background-color: transparent;
`;

const ToggleOn = styled(ToggleButton)`
  background-color: ${(p) => p.theme.palette.primary.dark};
  color: white;
  border: 1px solid ${(p) => p.theme.palette.primary.dark};

  svg {
    position: relative;
    top: 2px;
  }
`;

const AttendingToggle = ({
  isPast,
  eventId
}: {
  isPast: boolean;
  eventId: string;
}) => {
  const { user } = useCurrentUser();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (user) {
    return (
      <LoggedInAttendingToggle
        userId={user.id}
        eventId={eventId}
        isPast={isPast}
      />
    );
  } else {
    return (
      <>
        <LoggedOutToggle
          isPast={isPast}
          onClick={() => {
            setDialogOpen(true);
          }}
        />
        <SignupOrLoginDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
          }}
          initialState="login"
        />
      </>
    );
  }
};

const LoggedOutToggle = ({
  isPast,
  onClick
}: {
  isPast: boolean;
  onClick: (e: React.SyntheticEvent) => void;
}) => {
  if (isPast) {
    return null;
  }

  return (
    <ToggleOff title="RSVP to this event" disabled={isPast} onClick={onClick}>
      RSVP now
    </ToggleOff>
  );
};

const LoggedInAttendingToggle = ({
  userId,
  eventId,
  isPast
}: {
  userId: string;
  eventId: string;
  isPast: boolean;
}) => {
  const [rsvp] = useRsvpForEvent(eventId, userId);
  const isRsvped = !!rsvp;

  const onClick = (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (rsvp) {
      unRsvpForEvent(rsvp.id);
    } else {
      rsvpForEvent(eventId, userId);
    }
  };

  if (!isRsvped && isPast) {
    return null;
  }

  if (isRsvped) {
    return (
      <ToggleOn
        title="You're RSVP'd! See you there"
        onClick={onClick}
        disabled={isPast}
      >
        <Check size={12} /> {isPast ? 'You went' : `I'm going`}
      </ToggleOn>
    );
  }

  return (
    <ToggleOff title="RSVP to this event" disabled={isPast} onClick={onClick}>
      RSVP now
    </ToggleOff>
  );
};

const EventDateWrapper = styled('div')`
  border-radius: ${(p) => p.theme.shape.borderRadius}px;
  background-color: ${(p) => p.theme.palette.grey['600']};
  padding: 12px 24px;
`;

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const EventMonth = styled('span')`
  font-weight: 700;
  color: ${(p) => p.theme.palette.primary.main};
  text-transform: uppercase;
  font-size: 16px;
  text-align: center;
  margin-right: 6px;
`;

const EventDay = styled('span')`
  color: ${(p) => p.theme.palette.primary.main};
  text-align: center;
  font-size: 16px;
`;

const EventDate = ({ date }: { date: firebase.firestore.Timestamp }) => {
  const day = date.toDate().getDate();
  const month = months[date.toDate().getMonth()];

  return (
    <EventDateWrapper>
      <EventMonth>{month}</EventMonth>
      <EventDay>{day}</EventDay>
    </EventDateWrapper>
  );
};

const StyledA = styled('a')`
  ${({ theme }) => css`
    color: ${theme.palette.primary.main};
    font-size: ${theme.typography.h5.fontSize};
    font-weight: 700;

    @media (max-width: 800px) {
      font-size: ${theme.typography.h5.fontSize};
    }
  `};
`;

const EventFooter = styled('div')`
  position: absolute;
  bottom: 24px;
`;

const PaperInnerMini = styled('div')`
  position: relative;
  text-align: left;
  height: 365px;
  padding: ${(p) => p.theme.spacing(2)}px ${(p) => p.theme.spacing(2)}px
    ${(p) => p.theme.spacing(10)}px;

  @media (max-width: 800px) {
    height: 360px;
    padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(2)}px;
  }
`;

const PaperInner = styled('div')`
  text-align: left;
  height: 100%;
  padding: ${(p) => p.theme.spacing(2)}px ${(p) => p.theme.spacing(2)}px
    ${(p) => p.theme.spacing(18)}px;

  @media (max-width: 800px) {
    padding: ${(p) => p.theme.spacing(2)}px ${(p) => p.theme.spacing(2)}px
      ${(p) => p.theme.spacing(18)}px;
  }
`;

const EventMetaWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing(2)}px;
`;

const EventTimeWrapper = styled('time')`
  font-weight: 600;
  color: ${(p) => p.theme.palette.grey[300]};
  font-size: 1rem;
  display: inline-block;
`;

const EventTime = ({ date }: { date: firebase.firestore.Timestamp }) => {
  const timezone =
    typeof window !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'UTC';

  return (
    <EventTimeWrapper>
      {date
        .toDate()
        .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}{' '}
      ({timezone})
    </EventTimeWrapper>
  );
};

const EventTitleWrapper = styled('div')`
  width: calc(100% - 124px);
  margin-left: 12px;
  position: relative;

  @media (max-width: 800px) {
    width: 100%;
    margin: 0;
  }
`;

const EventTitle = styled('h2')`
  margin-top: 0;
  margin-bottom: ${(p) => p.theme.spacing(1)}px;
  font-size: 20px;
  line-height: 30px;
  color: ${(p) => p.theme.palette.grey[200]};
`;

const EventDescription = styled('p')`
  font-size: 1rem;
  line-height: 1.8rem;
  margin: 0;
  color: ${(p) => p.theme.palette.grey[300]};
`;

const EventWrapper = styled(Paper)`
  height: 100%;
  overflow: hidden;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const MiniEventWrapper = styled(Paper)`
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const EventPhoto = styled('div')`
  height: 250px;
  width: 100%;
  background-position: left center;
  background-size: cover;
  display: inline-block;
  margin: 0 auto;

  @media (max-width: 800px) {
    height: 250px;
  }
`;

const AttendeesWrapper = styled<'div', { hasAttendees: boolean }>('div')`
  margin-top: ${(p) => p.theme.spacing(5)}px;
  display: flex;
  align-items: center;
  height: ${(p) => (!p.hasAttendees ? '48px' : 'auto')};
`;

const RsvpCount = styled('span')`
  color: ${(p) => p.theme.palette.grey['400']};
  font-size: 1rem;
  display: inline-block;
  font-style: italic;
  margin-left: ${(p) => p.theme.spacing(2)}px;
`;

const useAllRsvpedUsers = (
  eventId: string
): LoadingValue<Doc<UserPublic>[]> => {
  const [rsvps, loadingRsvps, errorRsvps] = useRsvpsForEvent(eventId);
  const attendeeIds = useMemo(
    () => [...new Set((rsvps || EMPTY_ARR).map((r) => r.data.userId))],
    [rsvps]
  );
  const [users, loadingUsers, errorUsers] = useUsersPublicByIds(attendeeIds);
  const finalUsers =
    !loadingRsvps &&
    !loadingUsers &&
    rsvps &&
    users &&
    ((rsvps.length && users.length) || !rsvps.length)
      ? compact(users)
      : undefined;
  return [finalUsers, !finalUsers, errorRsvps || errorUsers];
};

const AttendeeGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridColumnGap: p.theme.spacing(3),
  gridRowGap: p.theme.spacing(3)
}));

const AvatarWithNameWrapper = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'left',
  alignItems: 'center',

  '> :not(:first-child)': {
    marginLeft: p.theme.spacing()
  }
}));

const AttendeeDialog = ({
  eventId,
  open,
  onClose
}: {
  eventId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const [users, loading] = useAllRsvpedUsers(eventId);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Attendees</DialogTitle>
      <DialogContent>
        {loading && <PlainLoader height={200} />}
        {users && (
          <AttendeeGrid>
            {sortBy(users, (u) => toUserName(u.data)).map((u) => (
              <AvatarWithNameWrapper key={u.id}>
                <Avatar
                  alt={u.data.firstName}
                  src={u.data.avatarUrl}
                  style={{ backgroundColor: '#333' }}
                >
                  {u.data.firstName.slice(0, 1).toUpperCase()}
                </Avatar>
                <div>{u.data.firstName}</div>
              </AvatarWithNameWrapper>
            ))}
          </AttendeeGrid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Attendees = ({
  event,
  isPast
}: {
  isPast: boolean;
  event: Doc<IEvent>;
}) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const [rsvps] = useRecentRsvpsForEvent(event.id);
  const attendeeIds = useMemo(
    () => (rsvps || EMPTY_ARR).map((r) => r.data.userId),
    [rsvps]
  );
  const [users] = useUserPublicMapByIds(attendeeIds);
  const { rsvpCount } = event.data;
  const attendeeCount = rsvpCount;

  if (!users || !attendeeIds) {
    return <AttendeesWrapper hasAttendees={false} />;
  }

  // we want a full range of ids, so that the MUI avatar group can figure out the correct count on its own
  const mockAttendeeIds = Array(rsvpCount)
    .fill(null)
    .map((_, i) => {
      return attendeeIds[i] || `mock-avatar-id-${i}`;
    });

  return (
    <AttendeesWrapper hasAttendees={attendeeIds.length > 0}>
      <AvatarGroup
        max={4}
        role="button"
        onClick={openDialog}
        style={{ cursor: 'pointer' }}
      >
        {mockAttendeeIds.map((aId) => (
          <Tooltip
            key={aId}
            placement="top"
            title={users[aId]?.data.firstName || ''}
          >
            <Avatar
              alt={users[aId]?.data.firstName}
              src={users[aId]?.data.avatarUrl}
              style={{ backgroundColor: '#333' }}
            >
              {users[aId]?.data.firstName.slice(0, 1).toUpperCase()}
            </Avatar>
          </Tooltip>
        ))}
      </AvatarGroup>
      {attendeeCount > 0 && (
        <RsvpCount>
          {attendeeCount} {isPast ? `went` : `RSVP'd`}
        </RsvpCount>
      )}
      {dialogOpen && (
        <AttendeeDialog
          eventId={event.id}
          open={dialogOpen}
          onClose={closeDialog}
        />
      )}
    </AttendeesWrapper>
  );
};

export const EventMeta = ({ d }: { d: Doc<IEvent> }) => {
  const {
    id: eventId,
    data: { name, date }
  } = d;
  const isPast = firebase.firestore.Timestamp.now() > date;
  return (
    <div>
      <EventMetaWrapper>
        <EventDate date={date} />
        <EventTitleWrapper>
          <EventTime date={date} />
        </EventTitleWrapper>
      </EventMetaWrapper>
      <EventTitle>{name}</EventTitle>
    </div>
  );
};

const DescriptionMini = styled('p')`
  font-size: 14px;
  color: ${(p) => p.theme.palette.grey[400]};
`;

export const MiniEvent: React.FC<Doc<IEvent>> = (eventDoc) => {
  const {
    data: { description, url, image, date }
  } = eventDoc;
  const isPast = firebase.firestore.Timestamp.now() > date;

  return (
    <MiniEventWrapper
      style={{
        opacity: isPast ? 0.5 : 1
      }}
    >
      <div>
        <EventPhoto
          style={{
            backgroundImage: `url(${image})`,
            height: '100%',
            minHeight: '250px'
          }}
        />
      </div>
      <PaperInnerMini>
        <EventMetaWrapper>
          <EventDate date={date} />
          <EventTitleWrapper>
            <EventTime date={date} />
          </EventTitleWrapper>
        </EventMetaWrapper>
        <EventTitle>{eventDoc.data.name}</EventTitle>
        <EventDescription>{description}</EventDescription>
        <EventFooter>
          <Attendees isPast={isPast} event={eventDoc} />
        </EventFooter>
      </PaperInnerMini>
    </MiniEventWrapper>
  );
};

export const Event: React.FC<Doc<IEvent>> = (eventDoc) => {
  const {
    data: { description, url, image, date }
  } = eventDoc;
  const isPast = firebase.firestore.Timestamp.now() > date;

  return (
    <div
      style={{
        height: '100%',
        position: 'relative'
      }}
    >
      <EventWrapper
        style={{
          opacity: isPast ? 0.5 : 1
        }}
      >
        <div>
          <EventPhoto style={{ backgroundImage: `url(${image})` }} />
        </div>
        <PaperInner>
          <EventMeta d={eventDoc} />
          <EventDescription>{description}</EventDescription>
          <EventFooter>
            <Attendees isPast={isPast} event={eventDoc} />
            <AttendingToggle isPast={isPast} eventId={eventDoc.id} />
            {url.length > 0 && (
              <EventDetails href={url} target="_blank">
                Event details <ExternalLink size={14} />
              </EventDetails>
            )}
          </EventFooter>
        </PaperInner>
      </EventWrapper>
    </div>
  );
};
